import { useEffect, useRef, useState } from "react";

import { useLocalStorage } from "./use-local-storage";

export type ThemeMode =
  | "system"
  | "light"
  | "dark"
  | "accessible-light"
  | "accessible-dark"
  | "theme-quill"
  | "theme-summi"
  | "theme-sage";

// Storage shape historically allowed `boolean | null` (legacy "is dark
// mode on?"). Normalize that to the modern ThemeMode on read so apps
// migrating from the old format keep working without a data migration.
function resolveStoredMode(raw: ThemeMode | boolean | null): ThemeMode {
  if (raw === null || raw === undefined) return "system";
  if (raw === true) return "dark";
  if (raw === false) return "light";
  return raw;
}

/**
 * Reads + writes the user's chosen theme mode from localStorage, normalising
 * any legacy `boolean | null` values along the way. `storageKey` lets each
 * app pick its own bucket (web vs admin keep independent preferences).
 */
export function useThemeMode(
  storageKey: string,
): [ThemeMode, React.Dispatch<ThemeMode>] {
  const [storedMode, setStoredModeRaw] = useLocalStorage<
    ThemeMode | boolean | null
  >(storageKey, null);

  const mode = resolveStoredMode(storedMode);

  // useLocalStorage's setter closes over storedValue, so it gets a new
  // identity every render. Indirecting through a ref breaks that closure
  // dependency, keeping setMode referentially stable — safe to include
  // in effect dependency arrays without re-firing.
  const setEnabledRef = useRef(setStoredModeRaw);
  useEffect(() => {
    setEnabledRef.current = setStoredModeRaw;
  });

  const setMode: React.Dispatch<ThemeMode> = (value: ThemeMode) => {
    setEnabledRef.current(value === "system" ? null : value);
  };

  return [mode, setMode];
}

/**
 * Resolves "system" against the OS preference so callers can apply
 * concrete CSS classes (`dark`, `accessible-dark`, etc.) to the DOM.
 */
export function useResolvedTheme(storageKey: string): ThemeMode {
  const [themeMode] = useThemeMode(storageKey);
  const prefersDarkMode = usePrefersDarkMode();

  if (themeMode === "system") {
    return prefersDarkMode ? "dark" : "light";
  }
  return themeMode;
}

/**
 * Boolean "is dark mode active right now?" helper. Writes flip the stored
 * mode between `dark` and `light` (does not preserve accessible/branded
 * variants — for that use `useThemeMode` directly).
 */
export function useDarkMode(
  storageKey: string,
): [boolean, React.Dispatch<boolean>] {
  const [themeMode, setThemeMode] = useThemeMode(storageKey);
  const prefersDarkMode = usePrefersDarkMode();

  const isDark =
    themeMode === "dark" ||
    themeMode === "accessible-dark" ||
    (themeMode === "system" && prefersDarkMode);

  const setEnabled: React.Dispatch<boolean> = (
    value: boolean | ((prev: boolean) => boolean),
  ) => {
    const resolved = value instanceof Function ? value(isDark) : value;
    setThemeMode(resolved ? "dark" : "light");
  };

  return [isDark, setEnabled];
}

// Detect dark mode preference via matchMedia. Re-fires when the OS-level
// preference flips so apps in "system" mode track it live.
function usePrefersDarkMode() {
  const [prefersDarkMode, setPrefersDarkMode] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQueryList = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersDarkMode(event.matches);
    };

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", handleChange);
    } else if (mediaQueryList.addListener) {
      mediaQueryList.addListener(handleChange);
    }

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener("change", handleChange);
      } else if (mediaQueryList.removeListener) {
        mediaQueryList.removeListener(handleChange);
      }
    };
  }, []);

  return prefersDarkMode;
}
